import * as React from 'react';
import { Component, createRef, ReactNode, RefObject } from 'react';

import {
    defineElements,
    H5PPlayerComponent,
    IxAPIEvent,
    IContext,
    IH5PInstance,
    IH5P
} from '@ai-content-hub/h5p-webcomponents';
import type {
    IPlayerModel,
    IContentMetadata,
    ILicenseData,
    IIntegration,
} from '@ai-content-hub/h5p-server';

/**
 * Drop H5P-server user-state endpoints from integration so the browser does not
 * GET/POST e.g. …/h5p/contentUserData/:contentId/state/0 (embed uses app/session APIs instead).
 */
function stripContentUserDataFromIntegration(integration: IIntegration): void {
    if (integration.ajax) {
        const ajax = integration.ajax as { contentUserData?: string; setFinished?: string };
        delete ajax.contentUserData;
    }
    const contents = integration.contents;
    if (contents) {
        for (const key of Object.keys(contents)) {
            const entry = contents[key] as { contentUserData?: unknown };
            delete entry.contentUserData;
        }
    }
    integration.saveFreq = false;
    integration.postUserStatistics = false;
}

/**
 * H5P core expects `integration.contents[cid].contentUserData` as
 * `[{ state: string }]` (JSON in `state`), matching the hub / ContentUserDataManager
 * shape — not a raw `getCurrentState()` value. See h5p-server H5PPlayer.render.test.
 */
function normalizeEmbedSessionContentUserDataForIntegration(raw: unknown): unknown {
    if (raw === undefined || raw === null) {
        return raw;
    }
    if (Array.isArray(raw)) {
        const first = raw[0];
        if (
            raw.length > 0 &&
            typeof first === 'object' &&
            first !== null &&
            'state' in first &&
            typeof (first as { state?: unknown }).state === 'string'
        ) {
            return raw;
        }
        return [{ state: JSON.stringify(raw) }];
    }
    if (typeof raw === 'string') {
        try {
            JSON.parse(raw);
            return [{ state: raw }];
        } catch {
            return [{ state: JSON.stringify(raw) }];
        }
    }
    return [{ state: JSON.stringify(raw) }];
}

/**
 * `/h5p/new/play` responses often expose scripts/styles under `cid-new` only.
 * Our real content id is a CMS UUID (`cid-{contentId}`) — inherit assets from the template row.
 */
function inheritH5pNewPlayTemplateArtifacts(
    contents: Record<string, unknown> | undefined,
    targetKey: string,
): Partial<IIntegrationContentEntry> {
    if (!contents) {
        return {};
    }
    const own = contents[targetKey] as Partial<IIntegrationContentEntry> | undefined;
    if (own?.scripts && own.scripts.length > 0) {
        return {};
    }
    const candidateKeys = [
        'cid-new',
        ...Object.keys(contents).filter((k) => k !== targetKey && k.startsWith('cid-')),
    ];
    for (const k of candidateKeys) {
        const e = contents[k] as Partial<IIntegrationContentEntry> | undefined;
        if (e?.scripts?.length || e?.styles?.length) {
            return {
                scripts: e.scripts,
                styles: e.styles,
                url: e.url,
                contentUrl: e.contentUrl,
                exportUrl: e.exportUrl,
                embedCode: e.embedCode,
                resizeCode: e.resizeCode,
            };
        }
    }
    return {};
}

/**
 * Value for `params.previousState` in serialized jsonContent (Blanks / MC read `contentData.previousState`).
 * Sources: session `{ answer }`, hub `[{ state: "<json>" }]`, raw Blanks cloze `string[]`.
 */
function extractPreviousStateForJsonContent(embedRaw: unknown): unknown | undefined {
    if (embedRaw === undefined || embedRaw === null) {
        return undefined;
    }
    if (typeof embedRaw === 'object' && !Array.isArray(embedRaw) && 'answer' in embedRaw) {
        const ans = (embedRaw as { answer: unknown }).answer;
        if (ans !== undefined && ans !== null) {
            return ans;
        }
    }
    if (!Array.isArray(embedRaw) || embedRaw.length === 0) {
        return undefined;
    }
    const first = embedRaw[0];
    if (
        typeof first === 'object' &&
        first !== null &&
        'state' in first &&
        typeof (first as { state?: unknown }).state === 'string'
    ) {
        try {
            return JSON.parse((first as { state: string }).state) as unknown;
        } catch {
            return undefined;
        }
    }
    if (embedRaw.every((x) => typeof x === 'string')) {
        return embedRaw as unknown[];
    }
    return undefined;
}

/**
 * CMS / API sometimes stores `params` as a JSON string; H5P.Blanks needs a real object
 * so we can spread `previousState` into it.
 */
function normalizeParamsForPreviousStateMerge(params: unknown): unknown {
    if (typeof params === 'string') {
        try {
            return JSON.parse(params) as unknown;
        } catch {
            return params;
        }
    }
    return params;
}

import { hasH5pLearnerInput, readH5pScore, requestH5pGrade } from './h5pGrading';
export {
    applyH5pBlanksSubmittedPresentation,
    applyH5pMultiChoiceSubmittedPresentation,
    applyH5pHistorySubmittedPresentation,
} from './h5pHistoryPresentation';
import { applyH5pHistorySubmittedPresentation } from './h5pHistoryPresentation';

function mergeParamsWithEmbedPreviousState(params: unknown, embedRaw: unknown | undefined): unknown {
    const normalized = normalizeParamsForPreviousStateMerge(params);
    if (typeof normalized !== 'object' || normalized === null || Array.isArray(normalized)) {
        return normalized;
    }
    const prev = extractPreviousStateForJsonContent(embedRaw);
    if (prev !== undefined) {
        return { ...normalized, previousState: prev };
    }
    return normalized;
}

interface IIntegrationContentEntry {
    displayOptions: {
        copy: boolean;
        copyright: boolean;
        embed: boolean;
        export: boolean;
        frame: boolean;
        icon: boolean;
    };
    fullScreen: '0' | '1';
    jsonContent: string;
    library: string;
    metadata?: ILicenseData;
    scripts?: string[];
    styles?: string[];
    url?: string;
    exportUrl?: string;
    embedCode?: string;
    resizeCode?: string;
    contentUrl?: string;
}

defineElements('h5p-player');

declare global {
    // eslint-disable-next-line @typescript-eslint/no-namespace
    namespace JSX {
        interface IntrinsicElements {
            'h5p-player': {
                'content-id'?: string;
                'context-id'?: string;
                /** Use string "true" | "false" — h5p-webcomponents compares to `'true'`. */
                'read-only-state'?: 'true' | 'false';
                'as-user-id'?: string;
                ref?: any;
            };
        }
    }
}

/**
 * H5P content data structure for the player
 * Contains the actual H5P content without server-specific model data
 */
export interface H5PPlayerContentData {
    /** H5P library identifier (e.g., "H5P.InteractiveVideo 1.27") */
    library: string;
    /** H5P content parameters */
    params: unknown;
    /** H5P content metadata */
    metadata: IContentMetadata;
}

interface IH5PPlayerUIProps {
    contentId: string;
    contextId?: string;
    asUserId?: string;
    readOnlyState?: boolean;
    /**
     * When true, keep `ajax.contentUserData` from the player model (H5P hub persistence).
     * Default false: strip user-state URLs so the client does not call the H5P server
     * `contentUserData` routes (use app/session storage instead).
     */
    useH5pContentUserData?: boolean;
    /**
     * Inline `contentUserData` for this content (e.g. restored from a content session snapshot).
     * Applied after stripping hub user-state URLs; does not enable server `contentUserData` requests.
     */
    embedSessionContentUserData?: unknown;
    /**
     * H5P content data passed directly from parent.
     * When provided, the player will use this data instead of requiring
     * the loadContentCallback to return it.
     */
    contentData?: H5PPlayerContentData;
    /**
     * Callback to load the player model from the server.
     * Required unless contentData is provided with a complete player model.
     * When contentData is provided, this callback should return a base player model
     * (scripts, styles, integration) and the content data will be merged in.
     */
    loadContentCallback?: (
        contentId: string,
        contextId?: string,
        asUserId?: string,
        readOnlyState?: boolean
    ) => Promise<IPlayerModel>;
    onInitialized?: (contentId: string) => void;
    onxAPIStatement?: (statement: any, context: any, event: IxAPIEvent) => void;
    /**
     * Session history embed: after init, run Blanks’ post-Check presentation (score, feedback).
     */
    historySubmittedPresentation?: boolean;
    /** ELMS-2370: LMS "hide answer" — never run the solution-revealing history path. */
    hideCorrectAnswers?: boolean;
    /**
     * Render the content in its own iframe instead of a div (EAA-1929). Set it when
     * SEVERAL activities share one page: div mode shares `window.H5P`,
     * `window.H5PIntegration` and the DOM id space between them.
     */
    forceIframeEmbed?: boolean;
}

export default class H5PPlayerUI extends Component<IH5PPlayerUIProps> {
    constructor(props: IH5PPlayerUIProps) {
        super(props);
        this.h5pPlayer = createRef();
    }

    private h5pPlayer: RefObject<H5PPlayerComponent>;

    /**
     * `h5p-player` only re-invokes `loadContentCallback` when the callback **reference**
     * changes (see web component setter). A single stable arrow would never re-merge
     * `embedSessionContentUserData` / `contentData` after async parent updates.
     * We cache a new function whenever the merged-input signature changes.
     */
    private mergedLoadSignatureCache = '';

    private mergedLoadCallbackCache:
        | ((
            contentId: string,
            contextId?: string,
            asUserId?: string,
            readOnlyState?: boolean,
        ) => Promise<IPlayerModel>)
        | null = null;

    /**
     * Invalidates a pending `queueMicrotask` from `componentDidMount` so we do
     * not assign `loadContentCallback` after unmount. Also ensures the native
     * `h5p-player` has received `content-id` / other attrs from React before
     * the first `render()` runs.
     */
    private serviceCallbackEpoch = 0;

    private getMergedLoadSignature(): string {
        try {
            return JSON.stringify({
                cid: this.props.contentId,
                embed: this.props.embedSessionContentUserData,
                data: this.props.contentData,
                hub: this.props.useH5pContentUserData,
                ro: this.props.readOnlyState,
                loader: this.props.loadContentCallback,
            });
        } catch {
            return `${this.props.contentId}-${String(this.props.embedSessionContentUserData)}`;
        }
    }

    private getMergedLoadCallback(): (
        contentId: string,
        contextId?: string,
        asUserId?: string,
        readOnlyState?: boolean,
    ) => Promise<IPlayerModel> {
        const sig = this.getMergedLoadSignature();
        if (sig !== this.mergedLoadSignatureCache || !this.mergedLoadCallbackCache) {
            this.mergedLoadSignatureCache = sig;
            this.mergedLoadCallbackCache = async (
                contentId: string,
                contextId?: string,
                asUserId?: string,
                readOnlyState?: boolean,
            ): Promise<IPlayerModel> => {
                if (!this.props.loadContentCallback) {
                    throw new Error(
                        'loadContentCallback is required when contentData is not provided with a complete player model',
                    );
                }

                let playerModel: IPlayerModel;
                try {
                    playerModel = await this.props.loadContentCallback(
                        contentId,
                        contextId,
                        asUserId,
                        readOnlyState,
                    );
                } catch (error) {
                    console.error('Failed to load player model:', error);
                    throw new Error(
                        `Failed to load H5P player model: ${error instanceof Error ? error.message : 'Unknown error'}`,
                    );
                }

                if (!playerModel.integration) {
                    throw new Error('Invalid player model: missing integration object');
                }

                // EAA-1929: H5P div mode puts every instance of a page in ONE document,
                // sharing `window.H5P`, `window.H5PIntegration` and the DOM id space. With
                // several activities of the same library on one page that leaks across
                // instances — a learner saw activity 1's sentences with activity 3's
                // dropdown options. Rendering each one in its own iframe restores the
                // isolation the single-embed case gets for free.
                if (this.props.forceIframeEmbed) {
                    playerModel.embedTypes = ['iframe'];
                }

                if (this.props.contentData) {
                    const { library, params, metadata } = this.props.contentData;

                    if (!library) {
                        throw new Error('Invalid content data: missing library');
                    }
                    if (!metadata) {
                        throw new Error('Invalid content data: missing metadata');
                    }
                    const contentKey = `cid-${contentId}`;

                    (playerModel as { contentId: string }).contentId = contentId;

                    if (!playerModel.integration.contents) {
                        playerModel.integration.contents = {};
                    }

                    const contentsMap = playerModel.integration.contents as Record<string, unknown>;
                    const templateArtifacts = inheritH5pNewPlayTemplateArtifacts(contentsMap, contentKey);
                    const existingContent = {
                        ...templateArtifacts,
                        ...((contentsMap[contentKey] || {}) as Partial<IIntegrationContentEntry>),
                    };

                    const defaultDisplayOptions: IIntegrationContentEntry['displayOptions'] = {
                        copy: false,
                        copyright: false,
                        embed: false,
                        export: false,
                        frame: false,
                        icon: false,
                    };

                    const licenseData: ILicenseData = {
                        title: metadata.title || '',
                        license: metadata.license || 'U',
                        defaultLanguage: metadata.defaultLanguage || 'en',
                        licenseVersion: metadata.licenseVersion,
                        contentType: metadata.contentType,
                        authors: metadata.authors,
                        source: metadata.source,
                        yearFrom: metadata.yearFrom,
                        yearTo: metadata.yearTo,
                        licenseExtras: metadata.licenseExtras,
                        authorComments: metadata.authorComments,
                        changes: metadata.changes,
                    };

                    const paramsForJson = mergeParamsWithEmbedPreviousState(
                        params,
                        this.props.embedSessionContentUserData,
                    );

                    const contentEntry: IIntegrationContentEntry = {
                        ...existingContent,
                        library,
                        jsonContent: JSON.stringify(paramsForJson),
                        metadata: licenseData,
                        displayOptions: existingContent.displayOptions || defaultDisplayOptions,
                        fullScreen: existingContent.fullScreen || '0',
                    };

                    playerModel.integration.contents[contentKey] = contentEntry as (typeof playerModel.integration.contents)[string];
                }

                if (readOnlyState) {
                    playerModel.integration.saveFreq = false;
                    playerModel.integration.postUserStatistics = false;
                }

                if (this.props.useH5pContentUserData !== true) {
                    stripContentUserDataFromIntegration(playerModel.integration);
                }

                if (
                    this.props.embedSessionContentUserData !== undefined &&
                    this.props.embedSessionContentUserData !== null &&
                    this.props.contentData &&
                    playerModel.integration.contents
                ) {
                    const normalizedUserData = normalizeEmbedSessionContentUserDataForIntegration(
                        this.props.embedSessionContentUserData,
                    );
                    const ck = `cid-${contentId}`;
                    const entry = playerModel.integration.contents[ck];
                    if (entry && typeof entry === 'object') {
                        (entry as { contentUserData?: unknown }).contentUserData = normalizedUserData;
                    }
                    // `/h5p/new/play` leaves a template row at `cid-new`; some H5P init paths still
                    // read learner state from that key alongside `cid-{uuid}`.
                    const cidNewEntry = playerModel.integration.contents['cid-new'];
                    if (cidNewEntry && typeof cidNewEntry === 'object') {
                        (cidNewEntry as { contentUserData?: unknown }).contentUserData =
                            normalizedUserData;
                    }
                }

                return playerModel;
            };
        }
        return this.mergedLoadCallbackCache;
    }

    public componentDidMount(): void {
        this.registerEvents();
        const epoch = ++this.serviceCallbackEpoch;
        queueMicrotask(() => {
            if (epoch !== this.serviceCallbackEpoch) {
                return;
            }
            this.setServiceCallbacks();
        });
    }

    public componentDidUpdate(): void {
        this.registerEvents();
        this.setServiceCallbacks();
    }

    public componentWillUnmount(): void {
        this.serviceCallbackEpoch += 1;
        this.unregisterEvents();
        this.destroyH5PEvents();
    }

    /**
     * The internal H5P instance object of the H5P content.
     *
     * Only available after the `initialized` event was fired. Important: This
     * object is only partially typed and there are more properties and methods
     * on it!
     */
    public get h5pInstance(): IH5PInstance | undefined {
        return this.h5pPlayer.current?.h5pInstance;
    }

    /**
     * The global H5P object / namespace (normally accessible through "H5P..."
     * or "window.H5P") of the content type. Depending on the embed type this
     * can be an object from the internal iframe, so you can use it to break the
     * barrier of the iframe and execute JavaScript inside the iframe.
     *
     * Only available after the `initialized` event was fired. Important: This
     * object is only partially typed and there are more properties and methods
     * on it!
     */
    public get h5pObject(): IH5P | undefined {
        return this.h5pPlayer.current?.h5pObject;
    }

    /**
     * Returns the copyright notice in HTML that you can insert somewhere to
     * display it. Undefined if there is no copyright information.
     */
    public getCopyrightHtml(): string {
        return this.h5pPlayer.current?.getCopyrightHtml() ?? '';
    }

    /**
     * Best-effort learner state for session persistence (embed localStorage / patch_answer).
     * Uses `getCurrentState()` on the H5P instance when present, else `contentUserData` from integration.
     */
    public getLearnerAnswerForSession(): unknown {
        const player = this.h5pPlayer.current;
        if (!player) {
            return null;
        }
        const inst = player.h5pInstance as { getCurrentState?: () => unknown } | undefined;
        if (inst && typeof inst.getCurrentState === 'function') {
            try {
                return inst.getCurrentState();
            } catch {
                // fall through to integration snapshot
            }
        }
        const win = player.h5pWindow as
            | {
                H5PIntegration?: {
                    contents?: Record<string, { contentUserData?: unknown }>;
                };
            }
            | undefined;
        const ck = `cid-${this.props.contentId}`;
        const raw = win?.H5PIntegration?.contents?.[ck]?.contentUserData;
        if (raw !== undefined && raw !== null) {
            return raw;
        }
        return null;
    }

    /**
     * Grade this activity now, as if the learner pressed "Check" (EAA-1929).
     * Scoped to THIS component's element because H5P div mode shares one document
     * across every instance on the page — see requestH5pGrade.
     */
    public requestGrade(): boolean {
        const player = this.h5pPlayer.current;
        if (!player) {
            return false;
        }
        return requestH5pGrade(
            player.h5pInstance,
            player as unknown as ParentNode,
        );
    }

    /** Current score without grading, or null when the learner never answered. */
    public getScoreSnapshot(): { score: number; max: number } | null {
        return readH5pScore(this.h5pPlayer.current?.h5pInstance);
    }

    /**
     * Did the learner put anything in? Ask BEFORE requestGrade — grading itself marks
     * the activity as answered, so afterwards an untouched question looks attempted and
     * would be persisted with a real 0.
     */
    public hasLearnerInput(): boolean {
        const player = this.h5pPlayer.current;
        if (!player) {
            return false;
        }
        return hasH5pLearnerInput(player.h5pInstance, this.getLearnerAnswerForSession());
    }

    public getSnapshotBeforeUpdate(): void {
        // Should the old editor instance be destroyed, we unregister from it...
        this.unregisterEvents();
        return null;
    }

    /**
     * @returns true if there is copyright information to be displayed.
     */
    public hasCopyrightInformation(): boolean {
        return this.h5pPlayer.current?.hasCopyrightInformation();
    }

    public render(): ReactNode {
        return (
            <h5p-player
                ref={this.h5pPlayer}
                content-id={this.props.contentId}
                context-id={this.props.contextId}
                as-user-id={this.props.asUserId}
                read-only-state={this.props.readOnlyState ? 'true' : 'false'}
            />
        );
    }

    /**
     * Displays the copyright notice in the regular H5P way.
     */
    public showCopyright(): void {
        this.h5pPlayer.current?.showCopyright();
    }

    /**
     * Asks the H5P content to resize itself inside the dimensions of the
     * container.
     *
     * Has no effect until the H5P object has fully initialized.
     */
    public resize(): void {
        this.h5pPlayer.current?.resize();
    }

    private onInitialized = (
        event: CustomEvent<{ contentId: string }>
    ): void => {
        if (this.props.onInitialized) {
            this.props.onInitialized(event.detail.contentId);
        }
        if (this.props.historySubmittedPresentation) {
            queueMicrotask(() => {
                requestAnimationFrame(() => {
                    applyH5pHistorySubmittedPresentation(this.h5pInstance, {
                        hideCorrectAnswers: this.props.hideCorrectAnswers,
                    });
                });
            });
        }
    };

    private onxAPIStatement = (
        event: CustomEvent<{
            context: IContext;
            event: IxAPIEvent;
            statement: any;
        }>
    ): void => {
        if (this.props.onxAPIStatement) {
            this.props.onxAPIStatement(
                event.detail.statement,
                event.detail.context,
                event.detail.event
            );
        }
    };

    private registerEvents(): void {
        this.h5pPlayer.current?.addEventListener(
            'initialized',
            this.onInitialized
        );
        if (this.props.onxAPIStatement) {
            this.h5pPlayer.current?.addEventListener(
                'xAPI',
                this.onxAPIStatement
            );
        }
    }

    private setServiceCallbacks(): void {
        if (this.h5pPlayer.current) {
            if (this.props.loadContentCallback) {
                this.h5pPlayer.current.loadContentCallback = this.getMergedLoadCallback();
            }
        }
    }

    private unregisterEvents(): void {
        this.h5pPlayer.current?.removeEventListener(
            'initialized',
            this.onInitialized
        );
        if (this.props.onxAPIStatement) {
            this.h5pPlayer.current?.removeEventListener(
                'xAPI',
                this.onxAPIStatement
            );
        }
    }

    private destroyH5PEvents(): void {
        const h5pObject = this.h5pObject;
        const contentId = this.props.contentId;

        if (h5pObject?.externalDispatcher) {
            // Ensure external dispatcher handlers are cleared on unmount.
            //
            // EAA-1929 — this MUST NOT be allowed to throw. H5P's EventDispatcher
            // validates with `!(listener instanceof Function)`, and `instanceof` is
            // realm-bound: for an iframe-embedded content `h5pObject` is the CHILD
            // window's H5P, so our parent-realm handler fails that check and `off()`
            // throws `TypeError: listener must be a function` — from
            // componentWillUnmount, which React turns into a crashed subtree. Observed
            // on the LMS as an empty attempt area with three of these in the console.
            //
            // The cleanup itself is best-effort anyway: the listeners this component
            // registers live on the `<h5p-player>` ELEMENT (see registerEvents), and in
            // iframe mode the child dispatcher never held them at all.
            try {
                h5pObject.externalDispatcher.off('initialized', this.onInitialized);
            } catch {
                /* cross-realm or already torn down */
            }
            try {
                h5pObject.externalDispatcher.off('xAPI', this.onxAPIStatement);
            } catch {
                /* cross-realm or already torn down */
            }
        }

        if (h5pObject?.instances && contentId) {
            // Remove instance to prevent stale event loops when editor+player coexist
            h5pObject.instances = h5pObject.instances.filter(
                (instance) => instance.contentId != contentId
            );
        }
    }
}
